import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { format, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { GearSix, X, ArrowsOutCardinal, Eye, EyeSlash } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { compareSeverity } from '../utils/styleHelpers';
import { fmtDateFR } from '../utils/dates';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { SortableWidget } from '../components/dashboard/SortableWidget';
import { AlertsWidget, EcheancesWidget } from '../components/dashboard/AlertWidgets';
import { FinanceSummaryWidget, CaMensuelWidget, TvaMensuelleWidget, CaAnnuelWidget } from '../components/dashboard/FinanceWidgets';
import { StatsHoursWidget, LeaveBalanceWidget, AbsencesWidget, RecentEntriesWidget } from '../components/dashboard/StatsWidgets';
import { PlanningWidget } from '../components/dashboard/PlanningWidget';
import { AnnouncementsWidget, AnnouncementModal } from '../components/dashboard/AnnouncementsSection';
import { BillingTrackingWidget } from '../components/dashboard/BillingTrackingWidget';
import { ReadyToBillWidget } from '../components/dashboard/ReadyToBillWidget';
import { ExpenseForecastWidget } from '../components/dashboard/ExpenseForecastWidget';
import { WeatherWidget } from '../components/dashboard/WeatherWidget';
import { RetentionsWidget } from '../components/dashboard/RetentionsWidget';
import { RentalsEndingWidget } from '../components/dashboard/RentalsEndingWidget';
import { MonthlyCostsWidget } from '../components/dashboard/MonthlyCostsWidget';
import { ProfitabilityWidget } from '../components/dashboard/ProfitabilityWidget';
import { EquipmentAlertsWidget } from '../components/dashboard/EquipmentAlertsWidget';
import { GlobalLaborCostWidget } from '../components/dashboard/GlobalLaborCostWidget';
import { ProjectLaborConsumptionWidget } from '../components/dashboard/ProjectLaborConsumptionWidget';
import ProjectDistributionCharts from '../components/dashboard/ProjectDistributionCharts';
import EmployeeHomeScreen from '../components/dashboard/EmployeeHomeScreen';
import AdminHomeScreen from '../components/dashboard/AdminHomeScreen';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES_FR = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const WIDGET_DEFS = {
  alerts: { label_fr: 'Alertes intelligentes', label_en: 'Smart Alerts' },
  echeances: { label_fr: 'Echeances a venir', label_en: 'Upcoming Deadlines' },
  stats_hours: { label_fr: 'Heures semaine / mois', label_en: 'Weekly / Monthly Hours' },
  leave_balance: { label_fr: 'Conges restants', label_en: 'Leave Balance' },
  absences: { label_fr: 'Absences du mois', label_en: 'Monthly Absences' },
  finance_summary: { label_fr: 'Synthese financiere', label_en: 'Financial Summary' },
  ca_annuel: { label_fr: "CA total de l'annee", label_en: 'Total Annual Revenue' },
  ca_mensuel: { label_fr: 'CA mensuel', label_en: 'Monthly Revenue' },
  tva_mensuelle: { label_fr: 'TVA mensuelle', label_en: 'Monthly VAT' },
  planning: { label_fr: 'Planning de la semaine', label_en: 'Weekly Planning' },
  recent_entries: { label_fr: 'Entrees recentes', label_en: 'Recent Entries' },
  announcements: { label_fr: 'Annonces equipe', label_en: 'Team Announcements' },
  billing_tracking: { label_fr: 'Suivi facturation & devis', label_en: 'Billing & Quotes Tracking' },
  ready_to_bill: { label_fr: 'Pret a facturer (avancement)', label_en: 'Ready to Bill (Progress)' },
  expense_forecast: { label_fr: 'Previsions depenses M+1', label_en: 'Expense Forecast M+1' },
  monthly_costs: { label_fr: 'Couts mensuels (12 mois)', label_en: 'Monthly Costs (12 months)' },
  profitability: { label_fr: 'Rentabilite par chantier', label_en: 'Profitability by Project' },
  equipment_alerts: { label_fr: 'Materiel a surveiller', label_en: 'Equipment Alerts' },
  global_labor: { label_fr: 'Cout MO global (chantiers)', label_en: 'Global Labor Cost (Projects)' },
  project_labor_consumption: { label_fr: 'Consommation MO par chantier (jours/heures)', label_en: 'Project Labor Consumption (days/hours)' },
  project_distribution: { label_fr: 'Repartition par chantier', label_en: 'Project Distribution' },
  retentions_to_claim: { label_fr: 'Retenues de garantie a recuperer', label_en: 'Retentions to Claim' },
  rentals_ending: { label_fr: 'Locations a renvoyer (< 7j)', label_en: 'Rentals Ending Soon' },
};

const DEFAULT_ORDER_ADMIN = ['weather', 'alerts', 'announcements', 'echeances', 'ready_to_bill', 'retentions_to_claim', 'rentals_ending', 'stats_hours', 'leave_balance', 'absences', 'ca_annuel', 'finance_summary', 'billing_tracking', 'global_labor', 'project_labor_consumption', 'project_distribution', 'profitability', 'equipment_alerts', 'expense_forecast', 'monthly_costs', 'ca_mensuel', 'tva_mensuelle', 'planning', 'recent_entries'];
const DEFAULT_ORDER_EMPLOYEE = ['weather', 'alerts', 'announcements', 'stats_hours', 'leave_balance', 'planning', 'recent_entries'];

const Dashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isAdmin = user?.role === 'admin';
  const dateFnsLocale = language === 'en' ? enUS : fr;
  const monthNames = language === 'en' ? MONTH_NAMES_EN : MONTH_NAMES_FR;
  const navigate = useNavigate();

  // Mobile/tablet detection for admin smartphone-style home screen (<1024px).
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [stats, setStats] = useState({ weeklyHours: 0, monthlyHours: 0, pendingEntries: 0, approvedEntries: 0 });
  const [recentEntries, setRecentEntries] = useState([]);
  const [weekPlanning, setWeekPlanning] = useState([]);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [absencesCalendar, setAbsencesCalendar] = useState([]);
  const [alertsData, setAlertsData] = useState([]);
  const [echeancesData, setEcheancesData] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', content: '', type: 'info', event_date: '', recipients: [] });
  const [allEmployees, setAllEmployees] = useState([]);

  const [widgetOrder, setWidgetOrder] = useState([]);
  const [widgetVisibility, setWidgetVisibility] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  const defaultWidgets = useMemo(() => isAdmin ? DEFAULT_ORDER_ADMIN : DEFAULT_ORDER_EMPLOYEE, [isAdmin]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load widget preferences
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/dashboard/widgets`, { withCredentials: true });
        const saved = res.data.widgets;
        if (saved && saved.length > 0) {
          const order = saved.map(w => w.widget_id).filter(id => defaultWidgets.includes(id));
          const missing = defaultWidgets.filter(id => !order.includes(id));
          setWidgetOrder([...missing, ...order]);
          const vis = {};
          saved.forEach(w => { vis[w.widget_id] = w.visible; });
          missing.forEach(id => { vis[id] = true; });
          setWidgetVisibility(vis);
        } else {
          setWidgetOrder(defaultWidgets);
          const vis = {};
          defaultWidgets.forEach(id => { vis[id] = true; });
          setWidgetVisibility(vis);
        }
      } catch (e) {
        setWidgetOrder(defaultWidgets);
        const vis = {};
        defaultWidgets.forEach(id => { vis[id] = true; });
        setWidgetVisibility(vis);
      }
      setPrefsLoaded(true);
    };
    loadPrefs();
  }, [defaultWidgets]);

  const savePrefs = useCallback(async (order, visibility) => {
    try {
      const widgets = order.map((id, idx) => ({
        widget_id: id, visible: visibility[id] !== false, order: idx
      }));
      await axios.put(`${API_URL}/api/dashboard/widgets`, { widgets }, { withCredentials: true });
    } catch (e) { console.error('Erreur sauvegarde preferences:', e); }
  }, [API_URL]);

  const fetchData = useCallback(async () => {
    try {
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const weekEnd = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const monthStart = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');

      const requests = [
        axios.get(`${API_URL}/api/time-entries?start_date=${weekStartStr}&end_date=${weekEnd}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/time-entries?start_date=${monthStart}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/time-entries`, { withCredentials: true }),
        axios.get(`${API_URL}/api/planning/week?week_start=${weekStartStr}`, { withCredentials: true })
      ];
      if (isAdmin) requests.push(axios.get(`${API_URL}/api/dashboard/finance`, { withCredentials: true }));

      const responses = await Promise.all(requests);
      const [weeklyRes, monthlyRes, recentRes, planningRes] = responses;

      setStats({
        weeklyHours: Math.round(weeklyRes.data.reduce((s, e) => s + e.total_hours, 0) * 100) / 100,
        monthlyHours: Math.round(monthlyRes.data.reduce((s, e) => s + e.total_hours, 0) * 100) / 100,
        pendingEntries: recentRes.data.filter(e => e.status === 'pending').length,
        approvedEntries: recentRes.data.filter(e => e.status === 'approved').length
      });
      setRecentEntries(recentRes.data.slice(0, 5));
      setWeekPlanning(planningRes.data);
      if (isAdmin && responses[4]) setFinance(responses[4].data);

      try {
        if (isAdmin) {
          const balRes = await axios.get(`${API_URL}/api/leaves/balance-all`, { withCredentials: true });
          const balances = Array.isArray(balRes.data) ? balRes.data : (balRes.data.balances || []);
          const totalUsed = balances.reduce((s, b) => s + b.used, 0);
          const totalRemaining = balances.reduce((s, b) => s + b.remaining, 0);
          setLeaveBalance({ total: balances[0]?.total || 25, used: totalUsed, remaining: totalRemaining, count: balances.length });
        } else {
          const uid = JSON.parse(atob(document.cookie.split('access_token=')[1]?.split('.')[1] || 'e30=')).sub || '';
          if (uid) {
            const balRes = await axios.get(`${API_URL}/api/leaves/balance/${uid}`, { withCredentials: true });
            setLeaveBalance(balRes.data);
          }
        }
      } catch (e) { console.error('Erreur solde conges:', e); }

      try {
        const now = new Date();
        const calRes = await axios.get(`${API_URL}/api/leaves/calendar?year=${now.getFullYear()}&month=${now.getMonth() + 1}`, { withCredentials: true });
        setAbsencesCalendar(calRes.data);
      } catch (e) { console.error('Erreur calendrier absences:', e); }

      try {
        const alertsRes = await axios.get(`${API_URL}/api/dashboard/alerts`, { withCredentials: true });
        setAlertsData(alertsRes.data.alerts || []);
      } catch (e) { console.error('Erreur alertes:', e); }

      if (isAdmin) {
        try {
          const deadlines = [];
          const compDocsRes = await axios.get(`${API_URL}/api/company-docs/expiry-alerts`, { withCredentials: true });
          (compDocsRes.data.alerts || []).forEach(a => {
            const detail = a.days_remaining != null ? `Expire dans ${a.days_remaining}j` : 'Manquant';
            const severity = (a.status === 'missing' || a.status === 'expired') ? 'danger' : 'warning';
            deadlines.push({ type: 'doc', label: a.label, detail, severity, date: a.expiry_date });
          });
          const empsRes = await axios.get(`${API_URL}/api/users/employees`, { withCredentials: true });
          const now2 = new Date();
          (empsRes.data || []).forEach(emp => {
            if (emp.contract_type === 'CDD' && emp.cdd_end_date) {
              const end = new Date(emp.cdd_end_date);
              const diff = Math.ceil((end - now2) / (1000 * 60 * 60 * 24));
              if (diff <= 60 && diff >= -7) {
                const detail = diff > 0 ? `Fin dans ${diff}j` : `Expire depuis ${-diff}j`;
                const severity = diff <= 0 ? 'danger' : diff <= 15 ? 'warning' : 'info';
                deadlines.push({ type: 'cdd', label: `CDD ${emp.name || emp.first_name}`, detail, severity, date: emp.cdd_end_date });
              }
            }
          });
          deadlines.sort((a, b) => compareSeverity(a.severity, b.severity));
          setEcheancesData(deadlines);
        } catch (e) { console.error('Erreur echeances:', e); }
      }

      try {
        const annRes = await axios.get(`${API_URL}/api/announcements`, { withCredentials: true });
        setAnnouncements(annRes.data || []);
      } catch (e) { console.error('Erreur annonces:', e); }

      if (isAdmin) {
        try {
          const empRes = await axios.get(`${API_URL}/api/users/employees`, { withCredentials: true });
          setAllEmployees(empRes.data || []);
        } catch (e) { console.error('Erreur employes:', e); }
      }
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, API_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/announcements`, annForm, { withCredentials: true });
      setShowAnnouncementModal(false);
      setAnnForm({ title: '', content: '', type: 'info', event_date: '', recipients: [] });
      fetchData();
    } catch (e) { toast.error('Erreur publication annonce'); }
  };

  const deleteAnnouncement = async (id) => {
    try { await axios.delete(`${API_URL}/api/announcements/${id}`, { withCredentials: true }); fetchData(); } catch (e) { toast.error('Erreur suppression annonce'); }
  };

  const getMonthLabel = (monthStr) => { const [, m] = monthStr.split('-'); return monthNames[parseInt(m, 10) - 1] || monthStr; };

  const openNavigation = (address) => {
    const enc = encodeURIComponent(address);
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) window.open(`maps://maps.apple.com/?daddr=${enc}`, '_blank');
    else if (/Android/.test(navigator.userAgent)) window.open(`google.navigation:q=${enc}`, '_blank');
    else window.open(`https://www.google.com/maps/dir/?api=1&destination=${enc}`, '_blank');
  };

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const getTasksForDay = (date) => weekPlanning.filter(t => t.date === format(date, 'yyyy-MM-dd'));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setWidgetOrder(prev => {
        const oldIdx = prev.indexOf(active.id);
        const newIdx = prev.indexOf(over.id);
        const newOrder = arrayMove(prev, oldIdx, newIdx);
        savePrefs(newOrder, widgetVisibility);
        return newOrder;
      });
    }
  };

  const toggleVisibility = (widgetId) => {
    setWidgetVisibility(prev => {
      const next = { ...prev, [widgetId]: !prev[widgetId] };
      savePrefs(widgetOrder, next);
      return next;
    });
  };

  const renderWidget = (widgetId) => {
    if (widgetVisibility[widgetId] === false && !isEditing) return null;
    const wrapStyle = widgetVisibility[widgetId] === false ? { opacity: 0.4, pointerEvents: 'none' } : {};

    switch (widgetId) {
      case 'weather': return <div style={wrapStyle}><WeatherWidget /></div>;
      case 'retentions_to_claim': return isAdmin ? <RetentionsWidget wrapStyle={wrapStyle} /> : null;
      case 'rentals_ending': return isAdmin ? <RentalsEndingWidget wrapStyle={wrapStyle} /> : null;
      case 'alerts': return <AlertsWidget wrapStyle={wrapStyle} alertsData={alertsData} language={language} onAction={fetchData} />;
      case 'echeances': return <EcheancesWidget wrapStyle={wrapStyle} echeancesData={echeancesData} language={language} />;
      case 'announcements': return <AnnouncementsWidget wrapStyle={wrapStyle} announcements={announcements} language={language} isAdmin={isAdmin} onCreateClick={() => setShowAnnouncementModal(true)} onDelete={deleteAnnouncement} />;
      case 'stats_hours': return <StatsHoursWidget wrapStyle={wrapStyle} stats={stats} language={language} />;
      case 'leave_balance': return <LeaveBalanceWidget wrapStyle={wrapStyle} leaveBalance={leaveBalance} isAdmin={isAdmin} language={language} />;
      case 'absences': return <AbsencesWidget wrapStyle={wrapStyle} absencesCalendar={absencesCalendar} language={language} />;
      case 'finance_summary': return <FinanceSummaryWidget wrapStyle={wrapStyle} finance={finance} language={language} />;
      case 'ca_annuel': return isAdmin ? <CaAnnuelWidget wrapStyle={wrapStyle} finance={finance} language={language} /> : null;
      case 'ca_mensuel': return <CaMensuelWidget wrapStyle={wrapStyle} finance={finance} language={language} getMonthLabel={getMonthLabel} />;
      case 'tva_mensuelle': return <TvaMensuelleWidget wrapStyle={wrapStyle} finance={finance} language={language} getMonthLabel={getMonthLabel} />;
      case 'planning': return <PlanningWidget wrapStyle={wrapStyle} weekDays={weekDays} getTasksForDay={getTasksForDay} openNavigation={openNavigation} dateFnsLocale={dateFnsLocale} language={language} />;
      case 'recent_entries': return <RecentEntriesWidget wrapStyle={wrapStyle} recentEntries={recentEntries} language={language} />;
      case 'billing_tracking': return isAdmin ? <BillingTrackingWidget wrapStyle={wrapStyle} /> : null;
      case 'ready_to_bill': return isAdmin ? <ReadyToBillWidget wrapStyle={wrapStyle} /> : null;
      case 'expense_forecast': return isAdmin ? <ExpenseForecastWidget wrapStyle={wrapStyle} /> : null;
      case 'monthly_costs': return isAdmin ? <MonthlyCostsWidget wrapStyle={wrapStyle} /> : null;
      case 'profitability': return isAdmin ? <ProfitabilityWidget wrapStyle={wrapStyle} /> : null;
      case 'equipment_alerts': return isAdmin ? <EquipmentAlertsWidget wrapStyle={wrapStyle} /> : null;
      case 'global_labor': return isAdmin ? <div style={wrapStyle}><GlobalLaborCostWidget /></div> : null;
      case 'project_labor_consumption': return isAdmin ? <div style={wrapStyle}><ProjectLaborConsumptionWidget /></div> : null;
      case 'project_distribution': return isAdmin ? <div style={wrapStyle}><ProjectDistributionCharts compact /></div> : null;
      default: return null;
    }
  };

  if (loading || !prefsLoaded) return <div className="loading-container"><div className="spinner" /></div>;

  // Employees see a smartphone-style icon grid instead of widgets.
  if (!isAdmin) return <EmployeeHomeScreen user={user} />;
  // Admin on mobile/tablet : same smartphone-style grid with sub-menus.
  if (isAdmin && isMobile) return <AdminHomeScreen user={user} />;

  const visibleWidgets = widgetOrder.filter(id => isEditing || widgetVisibility[id] !== false);

  return (
    <>
    <div data-testid="employee-dashboard">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">{language === 'en' ? 'Hello' : 'Bonjour'}, {user?.name}</h1>
          <p className="page-subtitle">{fmtDateFR(new Date())}</p>
        </div>
        <button
          data-testid="edit-dashboard-btn"
          className={`btn ${isEditing ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setIsEditing(!isEditing)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {isEditing ? <><X size={16} /> {language === 'en' ? 'Done' : 'Terminer'}</> : <><GearSix size={16} /> {language === 'en' ? 'Customize' : 'Personnaliser'}</>}
        </button>
      </div>

      {isEditing && (
        <div data-testid="widget-config-panel" className="card" style={{ marginBottom: '24px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <ArrowsOutCardinal size={18} />
            <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{language === 'en' ? 'Drag to reorder, click eye to show/hide' : 'Glissez pour reordonner, cliquez sur l\'oeil pour afficher/masquer'}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {widgetOrder.map(id => {
              const def = WIDGET_DEFS[id];
              if (!def) return null;
              const visible = widgetVisibility[id] !== false;
              const label = language === 'en' ? def.label_en : def.label_fr;
              return (
                <button
                  key={id}
                  data-testid={`toggle-widget-${id}`}
                  onClick={() => toggleVisibility(id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 14px', borderRadius: '8px', border: '1px solid #E2E2E6',
                    backgroundColor: visible ? 'var(--primary, #002FA7)' : '#F4F5F7',
                    color: visible ? 'white' : '#5E5F66',
                    cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                >
                  {visible ? <Eye size={14} /> : <EyeSlash size={14} />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleWidgets} strategy={verticalListSortingStrategy}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {visibleWidgets.map(id => {
              const content = renderWidget(id);
              if (!content) return null;
              return (
                <SortableWidget key={id} id={id} isEditing={isEditing}>
                  {content}
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>

    <AnnouncementModal
      show={showAnnouncementModal}
      onClose={() => setShowAnnouncementModal(false)}
      onSubmit={handleCreateAnnouncement}
      onRefresh={fetchData}
      annForm={annForm}
      setAnnForm={setAnnForm}
      allEmployees={allEmployees}
    />
    </>
  );
};

export default Dashboard;
